import { useCallback } from 'react';

import { useRouter } from 'next/router';

import * as uuid from 'uuid';
import useSWR, { SWRConfiguration } from 'swr';
import { useToast } from '@chakra-ui/react';

import { getSegmentFromCookies } from '~/shared/utils/getSegmentFromCookies';
import { api } from '~/shared/services/api';
import { routes } from '~/shared/constants/routes';

import { formatPaymentMethod } from '../utils/formatPaymentMethod';
import { formatCompany } from '../utils/formatCompany';
import { ICompanySettings } from '../interfaces/ICompanySettings';
import { ICompany } from '../interfaces/ICompany';
import { ICloneCompanyFormData } from '../interfaces/ICloneCompanyFormData';
import { CompanyRequestDTO } from '../dtos/CompanyRequestDTO';

interface UseCompanies {
  companies: ICompany[];
  update(id: string, data: CompanyRequestDTO): Promise<void>;
  create(data: CompanyRequestDTO): Promise<ICompany>;
  isLoading: boolean;
  checkCompanyAlreadyExists(document: string, id?: string): Promise<boolean>;
  cloneCompanyData(
    data: ICloneCompanyFormData,
    toCompany: string
  ): Promise<void>;
  updateCompanySettings(data: ICompanySettings, id: string): Promise<void>;
}

export const useCompanies = (
  options?: SWRConfiguration<ICompany[]>
): UseCompanies => {
  const router = useRouter();
  const segment = getSegmentFromCookies();
  const toast = useToast({
    position: 'top-right',
    duration: 2500,
  });

  const {
    data: companies,
    mutate,
    error,
  } = useSWR<ICompany[]>(`/segments/${segment}/companies`, options);

  const formattedCompanies =
    companies?.map((company) => formatCompany(company)) || [];

  const cloneCompanyData = useCallback(
    async (data: ICloneCompanyFormData, toCompany: string) => {
      const segmentId = getSegmentFromCookies();
      await api.post(
        `/segments/${segmentId}/companies/${data.fromCompany}/clone/${toCompany}`,
        data
      );

      mutate((old) =>
        old.map((company) => ({
          ...company,
          ...(company._id === toCompany && {
            cloningDataPending: true,
          }),
        }))
      );
    },
    [mutate]
  );

  const create = useCallback(
    async (data: CompanyRequestDTO): Promise<ICompany> => {
      const uuidV4 = uuid.v4();
      const segmentIdFromCookies = getSegmentFromCookies();
      const segmentId = segmentIdFromCookies || uuidV4;
      const response = await api.post(`/segments/${segmentId}/companies`, data);

      mutate((old) => {
        const newCompanies =
          old?.length > 0 ? [response.data, ...old] : [response.data];

        return newCompanies;
      }, false);

      return response.data;
    },
    [mutate]
  );

  const update = useCallback(
    async (id: string, data: CompanyRequestDTO) => {
      const response = await api.put(
        `/segments/${segment}/companies/${id}`,
        data
      );

      mutate((old) => {
        return old?.map((item) => {
          if (item._id === id) {
            return {
              ...item,
              ...response.data,
            };
          }

          return item;
        });
      }, false);
    },
    [mutate, segment]
  );

  const checkCompanyAlreadyExists = useCallback(
    async (document: string, id?: string) => {
      const response = await api.get(`/segments/${segment}/companies/check`, {
        params: {
          id,
          document,
        },
      });

      if (response.data) {
        return true;
      }

      return false;
    },
    [segment]
  );

  const updateCompanySettings = useCallback(
    async (data: ICompanySettings, companyId: string) => {
      try {
        const formattedData: ICompanySettings = {
          clonedFrom: data.clonedFrom || null,
          paymentMethods: data.paymentMethods.map(formatPaymentMethod),
          fallbackLinkProductId: data.fallbackLinkProductId || null,
          blockSalesOnNfceFailure: data.blockSalesOnNfceFailure || false,
          blockSalesOnNfceFailureDeadlineDays: data.blockSalesOnNfceFailure
            ? data.blockSalesOnNfceFailureDeadlineDays
            : null,
        };

        await api.put(
          `/segments/${segment}/companies/${companyId}/settings`,
          formattedData
        );

        toast({
          title: 'Boa!',
          description: 'Configurações da empresa atualizadas com sucesso.',
          status: 'success',
        });

        router.push(routes.companies.list);
      } catch (updateError) {
        const errorMessage =
          updateError?.response?.data?.displayMessage ||
          updateError?.response?.data?.message ||
          'Erro ao atualizar as configurações da empresa.';

        toast({
          title: 'Ops!',
          description: errorMessage,
          status: 'error',
        });
      }
    },
    [router, segment, toast]
  );

  return {
    companies: formattedCompanies,
    isLoading: !error && !companies,
    update,
    create,
    checkCompanyAlreadyExists,
    cloneCompanyData,
    updateCompanySettings,
  };
};
